import moment, {type Moment} from 'moment-timezone';

import {hasValue} from '@famly/stat_ts-utils_has-value';
import {head, isNonEmpty} from 'web-app/util/non-empty-array';
import {exhaustiveCheck} from '@famly/stat_ts-utils_exhaustive-check';

import {
    biweeklyBillingStartDate,
    biweeklyPeriods,
    monthlyPeriods,
    weeklyPeriods,
    calculateFirstBillingPeriodStartDate,
    getClosestBiweeklyStartDate,
} from '../helpers/create-billing-periods';
import {isRegistrationDateInTheFuture} from '../helpers/registration-date';
import type * as Types from '../types';

type Args = {
    selectedChildren: Array<Types.Child>;
    billingFrequencies: Array<Types.BillingFrequency>;
    view: Types.CompoundView;
};
type Result = {
    monthlyBillingPeriods: null | Types.MonthlyPeriods;
    weeklyBillingPeriods: null | Types.WeeklyPeriods;
    biweeklyBillingPeriods: null | Types.BiweeklyPeriods;
};
export const initialBillingPeriods = ({selectedChildren, billingFrequencies, view}: Args): Result => {
    const startDates: {monthly: Moment; weekly: Moment; biweekly: Moment} = (() => {
        const isSingleChildCreateView = view.view === 'create' && view.subView === 'single';
        const lastPlan = selectedChildren.length === 1 ? getLastPlan(selectedChildren[0]) : null;
        if (isSingleChildCreateView && hasValue(lastPlan)) {
            const limitFirstUnbilledDate = limitBackOptional(lastPlan.firstUnbilledDate);
            if (!hasValue(lastPlan.endDate)) {
                // For open ended plans: start options at the next period (but no earlier than the first unbilled date).
                return {
                    monthly: currentOrNextPeriod(
                        limitFirstUnbilledDate(nextPeriod(lastPlan.startDate, 'monthly', billingFrequencies)),
                        'monthly',
                        billingFrequencies,
                    ),
                    weekly: currentOrNextPeriod(
                        limitFirstUnbilledDate(nextPeriod(lastPlan.startDate, 'weekly', billingFrequencies)),
                        'weekly',
                        billingFrequencies,
                    ),
                    biweekly: currentOrNextPeriod(
                        limitFirstUnbilledDate(nextPeriod(lastPlan.startDate, 'biweekly', billingFrequencies)),
                        'biweekly',
                        billingFrequencies,
                    ),
                };
            }

            // For a closed ended plan: start options right after the end date.
            return {
                monthly: currentOrNextPeriod(lastPlan.endDate, 'monthly', billingFrequencies),
                weekly: currentOrNextPeriod(lastPlan.endDate, 'weekly', billingFrequencies),
                biweekly: currentOrNextPeriod(lastPlan.endDate, 'biweekly', billingFrequencies),
            };
        } else {
            const singleSelectedChild =
                isNonEmpty(selectedChildren) && selectedChildren.length === 1 ? head(selectedChildren) : null;
            const isRegistrationDateBillingPeriodStartDate = (
                registrationDate: undefined | Types.Child['startDate'],
            ): registrationDate is NonNullable<Types.Child['startDate']> =>
                hasValue(registrationDate) && isRegistrationDateInTheFuture(registrationDate);

            const periodStartDate = isRegistrationDateBillingPeriodStartDate(singleSelectedChild?.startDate)
                ? singleSelectedChild.startDate
                : moment();
            const biweeklyPeriodStartDate =
                hasValue(singleSelectedChild) &&
                hasValue(singleSelectedChild.startDate) &&
                isRegistrationDateInTheFuture(singleSelectedChild.startDate)
                    ? biweeklyBillingStartDate({billingFrequencies, defaultStartDate: singleSelectedChild.startDate})
                    : biweeklyBillingStartDate({billingFrequencies});
            return {monthly: periodStartDate, weekly: periodStartDate, biweekly: biweeklyPeriodStartDate};
        }
    })();

    return {
        monthlyBillingPeriods: monthlyPeriods(startDates.monthly),
        weeklyBillingPeriods: weeklyPeriods(startDates.weekly),
        biweeklyBillingPeriods: biweeklyPeriods(startDates.biweekly),
    };
};

/**
 * Returns the last plan of the child (the plan with the latest start date).
 */
const getLastPlan = (child: Types.Child): Types.Plan | null => {
    if (child.plans.length === 0) {
        return null;
    }
    return child.plans
        .slice(1)
        .reduce((plan1, plan2) => (plan1.startDate.isAfter(plan2.startDate) ? plan1 : plan2), child.plans[0]);
};

/**
 * Returns the next period aligned start date strictly after `date`.
 */
const nextPeriod = (
    date: Moment,
    frequency: Types.InitialBillingFrequency,
    billingFrequencies: Types.BillingFrequency[],
): Moment => addPeriods(alignBack(date, frequency, billingFrequencies), 1, frequency);

/**
 * Return the earliest period aligned start date at or after `date`.
 */
const currentOrNextPeriod = (
    date: Moment,
    frequency: Types.InitialBillingFrequency,
    billingFrequencies: Types.BillingFrequency[],
): Moment => {
    if (isAligned(date, frequency, billingFrequencies)) {
        return date;
    }
    return nextPeriod(date, frequency, billingFrequencies);
};

/**
 * Aligns a date backwards to the start date of the period that contains the date.
 */
const alignBack = (
    date: Moment,
    frequency: Types.InitialBillingFrequency,
    billingFrequencies: Types.BillingFrequency[],
): Moment => {
    switch (frequency) {
        case 'monthly':
            return date.clone().startOf('month');
        case 'weekly':
            return date.clone().startOf('isoWeek');
        case 'biweekly': {
            const biweeklyBillingFrequency = billingFrequencies.find(frequency => frequency.type === 'biweekly');
            if (!hasValue(biweeklyBillingFrequency) || !hasValue(biweeklyBillingFrequency.autoBillingPeriod)) {
                return date;
            }
            return getClosestBiweeklyStartDate(
                calculateFirstBillingPeriodStartDate(
                    moment(biweeklyBillingFrequency.startDate),
                    biweeklyBillingFrequency.autoBillingPeriod,
                ),
                date,
            );
        }
        default:
            exhaustiveCheck(frequency);
            return date;
    }
};

/**
 * True iff `date` is aligned to a period start.
 */
const isAligned = (
    date: Moment,
    frequency: Types.InitialBillingFrequency,
    billingFrequencies: Types.BillingFrequency[],
): boolean => alignBack(date, frequency, billingFrequencies).isSame(date, 'date');

/**
 * Adds `n` periods to `date`.
 *
 * Note that the resulting date will be period aligned iff the input date is.
 */
const addPeriods = (date: Moment, n: number, frequency: Types.InitialBillingFrequency): Moment => {
    switch (frequency) {
        case 'monthly':
            return date.clone().add(n, 'months');
        case 'weekly':
            return date.clone().add(n, 'weeks');
        case 'biweekly':
            return date.clone().add(2 * n, 'weeks');
        default:
            exhaustiveCheck(frequency);
            return date;
    }
};

/**
 * Limits a date to an optional earliest allowed date. There is no limit if `earliest` is null.
 */
const limitBackOptional: (earliest: Moment | null) => (date: Moment) => Moment = earliest => {
    if (!hasValue(earliest)) {
        return date => date;
    }
    return date => {
        if (earliest.isAfter(date)) {
            return earliest;
        }
        return date;
    };
};
